import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TooltipModule } from 'primeng/tooltip';
import { AssistantPulse, AssistantPulseTenant } from '@/cloud/models/assistant.models';
import { TranslationService } from '@/layout/service/translation.service';
import { fill } from '@/layout/service/assistant.translations';

type Health = 'critical' | 'down' | 'warn' | 'ok';

/**
 * The helpdesk status panel beside the full-page service desk chat: headline figures across every tenant, and the
 * tenants having the worst day with what is wrong at each. Every figure and tenant asks the assistant about itself;
 * a tenant can also be opened in the dashboard.
 */
@Component({
    selector: 'app-service-desk-overview',
    standalone: true,
    imports: [RouterLink, TooltipModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <header class="sdo-head">
            <div>
                <h2>{{ t()('serviceDeskPage.overview') }}</h2>
                <span class="sdo-updated" [class.stale]="stale()">
                    <span class="live-dot" aria-hidden="true"></span>
                    {{ updatedText() }}
                </span>
            </div>
            <button type="button" class="sdo-refresh" [class.spinning]="loading()" (click)="refresh.emit()" [disabled]="loading()" [attr.aria-label]="t()('serviceDeskPage.refresh')" [pTooltip]="t()('serviceDeskPage.refresh')" tooltipPosition="left">
                <i class="pi pi-refresh" aria-hidden="true"></i>
            </button>
        </header>

        @if (pulse(); as p) {
            <p class="sdo-headline">{{ p.headline }}</p>
            <div class="sdo-stats">
                @for (s of p.stats; track s.key) {
                    <button type="button" class="sdo-stat" [attr.data-tone]="s.value > 0 ? s.tone : 'success'" (click)="ask.emit(s.prompt)" [pTooltip]="s.prompt" tooltipPosition="bottom">
                        <span class="value">{{ s.value }}</span>
                        <span class="label">{{ s.label }}</span>
                        <i class="pi pi-sparkles ask-hint" aria-hidden="true"></i>
                    </button>
                }
            </div>

            <h3 class="sdo-label">
                {{ t()('assistant.tenantsTitle') }}
                @if (attention().length) {
                    <span class="count">{{ attention().length }}</span>
                }
            </h3>
            @if (attention().length) {
                <ol class="sdo-tenants">
                    @for (tn of attention(); track tn.namespaceId) {
                        <li class="sdo-tenant" [attr.data-health]="health(tn)">
                            <button type="button" class="tenant-ask" (click)="ask.emit(askAbout(tn.name))" [attr.aria-label]="askAbout(tn.name)">
                                <span class="tenant-top">
                                    <span class="health-dot" aria-hidden="true"></span>
                                    <span class="tenant-name">{{ tn.name }}</span>
                                    <span class="tenant-devices">{{ devicesText(tn.devices) }}</span>
                                </span>
                                <span class="bar" aria-hidden="true"><span [style.width.%]="onlineShare(tn)"></span></span>
                                <span class="chips">
                                    @for (c of reasons(tn); track c.key) {
                                        <span class="chip" [attr.data-tone]="c.tone">{{ c.text }}</span>
                                    }
                                </span>
                            </button>
                            <a class="tenant-open" [routerLink]="['/dashboard', tn.namespaceId]" [attr.aria-label]="openLabel(tn.name)" [pTooltip]="openLabel(tn.name)" tooltipPosition="left">
                                <i class="pi pi-arrow-up-right" aria-hidden="true"></i>
                            </a>
                        </li>
                    }
                </ol>
            } @else {
                <div class="sdo-clear">
                    <i class="pi pi-check-circle" aria-hidden="true"></i>
                    <span>{{ t()('serviceDeskPage.allClear') }}</span>
                </div>
            }
            @if (healthyCount() > 0) {
                <p class="sdo-healthy">{{ healthyText() }}</p>
            }
        } @else if (loading()) {
            <div class="sdo-stats">
                @for (i of [1, 2, 3, 4]; track i) {
                    <span class="sdo-stat skeleton"></span>
                }
            </div>
            @for (i of [1, 2, 3]; track i) {
                <span class="sdo-tenant skeleton"></span>
            }
        } @else {
            <p class="sdo-error"><i class="pi pi-exclamation-circle" aria-hidden="true"></i>{{ t()('serviceDeskPage.loadError') }}</p>
        }
    `,
    styleUrl: './service-desk-overview.component.scss'
})
export class ServiceDeskOverviewComponent {
    readonly t = inject(TranslationService).t;
    readonly pulse = input<AssistantPulse | null>(null);
    readonly loading = input(false);
    /** Wall-clock time from the page, so "updated 2m ago" moves without a refetch. */
    readonly now = input(Date.now());
    readonly ask = output<string>();
    readonly refresh = output<void>();

    readonly attention = computed(() => (this.pulse()?.tenants ?? []).filter((t) => t.attentionScore > 0 || t.offline > 0));
    readonly healthyCount = computed(() => (this.pulse()?.tenants.length ?? 0) - this.attention().length);
    readonly healthyText = computed(() => fill(this.t()('serviceDeskPage.healthy'), { count: this.healthyCount() }));

    private readonly ageSeconds = computed(() => {
        const generated = this.pulse()?.generatedUtc;
        return generated ? Math.max(0, Math.round((this.now() - Date.parse(generated)) / 1000)) : null;
    });
    /** Older than two refresh cycles: the live dot goes grey. */
    readonly stale = computed(() => (this.ageSeconds() ?? 0) > 150);
    readonly updatedText = computed(() => {
        const age = this.ageSeconds();
        if (age === null) return this.t()('assistant.pulseLoading');
        if (age < 10) return this.t()('serviceDeskPage.updatedNow');
        const ago = age < 60 ? `${age}s` : age < 3600 ? `${Math.floor(age / 60)}m` : `${Math.floor(age / 3600)}h`;
        return fill(this.t()('serviceDeskPage.updated'), { ago });
    });

    health(tn: AssistantPulseTenant): Health {
        if ((tn.critical ?? 0) > 0) return 'critical';
        if (tn.offline > 0) return 'down';
        if (tn.attentionScore > 0) return 'warn';
        return 'ok';
    }

    onlineShare(tn: AssistantPulseTenant): number {
        return tn.devices ? Math.round(((tn.devices - tn.offline) / tn.devices) * 100) : 100;
    }

    /** What is wrong at a tenant, worst first, as short chips. */
    reasons(tn: AssistantPulseTenant): { key: string; text: string; tone: string }[] {
        const out: { key: string; text: string; tone: string }[] = [];
        const add = (key: string, count: number | undefined, tone: string) => {
            if (count) out.push({ key, text: fill(this.t()(`serviceDeskPage.reason.${key}`), { count }), tone });
        };
        add('critical', tn.critical, 'danger');
        add('offline', tn.offline, 'danger');
        add('errors', tn.errors, 'warn');
        add('checkIn', tn.checkInIssues, 'warn');
        add('never', tn.neverCheckedIn, 'info');
        add('warnings', tn.warnings, 'info');
        return out.slice(0, 3);
    }

    devicesText(count: number): string {
        return fill(this.t()(count === 1 ? 'serviceDeskPage.device' : 'serviceDeskPage.devices'), { count });
    }

    askAbout(name: string): string {
        return `What's going on at ${name}?`;
    }

    openLabel(name: string): string {
        return fill(this.t()('serviceDeskPage.openTenant'), { name });
    }
}
